// ═══════════════════════════════════════════════════════════════
// PIXALLOY — PSD Export Module
// ═══════════════════════════════════════════════════════════════
// Converts PixAlloy's layer tree into a PSD file using ag-psd.
// Supports raster layers, groups, blend modes, opacity, visibility,
// masks, and layer positioning.
//
// Usage:
//   import { exportPSD } from './psd-export.js';
//   const blob = exportPSD(engine.s.layers, engine.s.w, engine.s.h);
//   // blob is a Blob of the PSD file
// ═══════════════════════════════════════════════════════════════

import { writePsd } from 'ag-psd';
import { ensurePsdCanvasInit } from './psd-canvas-init.js';

function ensureCanvasInit() { ensurePsdCanvasInit(); }

// ── Reverse blend mode mapping: Canvas2D → PSD blend mode ──
const CANVAS_TO_PSD_BLEND = {
  'source-over': 'normal',
  'multiply':    'multiply',
  'screen':      'screen',
  'overlay':     'overlay',
  'darken':      'darken',
  'lighten':     'lighten',
  'color-dodge': 'color dodge',
  'color-burn':  'color burn',
  'hard-light':  'hard light',
  'soft-light':  'soft light',
  'difference':  'difference',
  'exclusion':   'exclusion',
  'hue':         'hue',
  'saturation':  'saturation',
  'color':       'color',
  'luminosity':  'luminosity',
};

function mapBlendToPSD(canvasBlend) {
  return CANVAS_TO_PSD_BLEND[canvasBlend || 'source-over'] || 'normal';
}

/**
 * Find the bounding box of non-transparent pixels in a canvas.
 * Returns {left, top, right, bottom} or null if entirely transparent.
 * This is used to trim layer data for efficient PSD storage.
 */
function findContentBounds(ctx, fullW, fullH) {
  try {
    const id = ctx.getImageData(0, 0, fullW, fullH);
    const d = id.data;
    let minX = fullW, minY = fullH, maxX = 0, maxY = 0;
    for (let y = 0; y < fullH; y++) {
      for (let x = 0; x < fullW; x++) {
        if (d[(y * fullW + x) * 4 + 3] > 0) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < minX) return null; // fully transparent
    return { left: minX, top: minY, right: maxX + 1, bottom: maxY + 1 };
  } catch {
    return { left: 0, top: 0, right: fullW, bottom: fullH };
  }
}

/**
 * Convert a PixAlloy layer to an ag-psd Layer object.
 */
function convertLayer(layer, docW, docH) {
  const psdLayer = {
    name: layer.name || 'Layer',
    blendMode: mapBlendToPSD(layer.blend),
    opacity: layer.op ?? 1,
    hidden: !layer.vis,
  };

  if (layer.type === 'group') {
    psdLayer.children = [];
    psdLayer.opened = !layer.collapsed;
    return psdLayer;
  }

  // Text layer — write text data for non-destructive PSD roundtrip
  if (layer.textData) {
    const td = layer.textData;

    // If we have the original ag-psd text data, use it for lossless export
    if (layer._psdTextData) {
      psdLayer.text = { ...layer._psdTextData };
      // Update the text content in case the user edited it
      psdLayer.text.text = td.text || psdLayer.text.text;
    } else {
      // Build text data from PixAlloy's textData
      // Parse color string to RGBA
      let fillColor = { r: 0, g: 0, b: 0 };
      if (td.color) {
        const m = td.color.match(/(\d+)/g);
        if (m && m.length >= 3) {
          fillColor = { r: +m[0], g: +m[1], b: +m[2] };
        } else if (td.color.startsWith('#')) {
          const hex = td.color.replace('#', '');
          fillColor = {
            r: parseInt(hex.substring(0, 2), 16),
            g: parseInt(hex.substring(2, 4), 16),
            b: parseInt(hex.substring(4, 6), 16),
          };
        }
      }

      psdLayer.text = {
        text: td.text || '',
        left: td.x ?? 0,
        top: td.y ?? 0,
        right: (td.x ?? 0) + docW,
        bottom: (td.y ?? 0) + (td.size || 32) * 2,
        style: {
          font: { name: td.font || 'sans-serif' },
          fontSize: td.size || 32,
          fauxBold: td.weight === 'bold',
          fillColor,
        },
      };
    }
  }

  // For raster layers, provide the canvas with pixel data.
  // ag-psd reads the canvas directly when initializeCanvas is set.
  // We need to set top/left/bottom/right to define positioning.
  const bounds = findContentBounds(layer.x, docW, docH);
  if (bounds) {
    psdLayer.left = bounds.left;
    psdLayer.top = bounds.top;
    psdLayer.right = bounds.right;
    psdLayer.bottom = bounds.bottom;

    // Create a trimmed canvas containing only the content region
    const tw = bounds.right - bounds.left;
    const th = bounds.bottom - bounds.top;
    if (tw > 0 && th > 0) {
      const trimmed = new OffscreenCanvas(tw, th);
      const tx = trimmed.getContext('2d');
      tx.drawImage(layer.c, -bounds.left, -bounds.top);
      psdLayer.canvas = trimmed;
    }
  } else {
    // Fully transparent layer — still include it with dimensions
    psdLayer.left = 0;
    psdLayer.top = 0;
    psdLayer.right = 1;
    psdLayer.bottom = 1;
    const empty = new OffscreenCanvas(1, 1);
    psdLayer.canvas = empty;
  }

  // Layer mask
  if (layer.maskEnabled && layer.mask) {
    const maskBounds = findContentBounds(layer.maskCtx, docW, docH);
    if (maskBounds) {
      const mw = maskBounds.right - maskBounds.left;
      const mh = maskBounds.bottom - maskBounds.top;
      if (mw > 0 && mh > 0) {
        const maskCanvas = new OffscreenCanvas(mw, mh);
        const mx = maskCanvas.getContext('2d');
        mx.drawImage(layer.mask, -maskBounds.left, -maskBounds.top);
        psdLayer.mask = {
          left: maskBounds.left,
          top: maskBounds.top,
          right: maskBounds.right,
          bottom: maskBounds.bottom,
          canvas: maskCanvas,
          disabled: false,
        };
      }
    }
  }

  return psdLayer;
}

/**
 * Export PixAlloy layers as a PSD file.
 *
 * @param {Array} layers — PixAlloy layer objects (bottom-to-top order)
 * @param {number} width — document width
 * @param {number} height — document height
 * @param {Object} [options] — optional: { flatten: boolean }
 * @returns {Blob} — PSD file as a Blob
 */
export function exportPSD(layers, width, height, options = {}) {
  ensureCanvasInit();

  const psd = {
    width,
    height,
  };

  if (options.flatten) {
    // Flatten mode: composite all visible layers into a single "Background" layer
    const comp = new OffscreenCanvas(width, height);
    const cx = comp.getContext('2d');
    for (const l of layers) {
      if (!l.vis || l.type === 'group') continue;
      cx.globalAlpha = l.op ?? 1;
      cx.globalCompositeOperation = l.blend || 'source-over';
      cx.drawImage(l.c, 0, 0);
      cx.globalAlpha = 1;
      cx.globalCompositeOperation = 'source-over';
    }
    psd.children = [{
      name: 'Background',
      canvas: comp,
      left: 0,
      top: 0,
      right: width,
      bottom: height,
    }];
  } else {
    // ag-psd children order matches PixAlloy (bottom-to-top)
    const psdChildren = [];
    for (const layer of layers) {
      psdChildren.push(convertLayer(layer, width, height));
    }
    psd.children = psdChildren;
  }

  // Generate composite image data (the "flattened" preview that PSD files store)
  const compositeCanvas = new OffscreenCanvas(width, height);
  const ccx = compositeCanvas.getContext('2d');
  for (const l of layers) {
    if (!l.vis || l.type === 'group') continue;
    ccx.globalAlpha = l.op ?? 1;
    ccx.globalCompositeOperation = l.blend || 'source-over';
    ccx.drawImage(l.c, 0, 0);
    ccx.globalAlpha = 1;
    ccx.globalCompositeOperation = 'source-over';
  }
  psd.canvas = compositeCanvas;

  // Write PSD
  // Check if any text layers were created or edited in PixAlloy (not from PSD).
  // If so, tell Photoshop to re-render text on open since our bitmap may not match.
  const hasEditedText = layers.some(l => l.textData && !l._psdTextData);

  const buffer = writePsd(psd, {
    generateThumbnail: true,
    trimImageData: true,
    invalidateTextLayers: hasEditedText,
  });

  return new Blob([buffer], { type: 'application/octet-stream' });
}

/**
 * Flatten all visible layers into a single layer (in-place).
 * Returns the composited OffscreenCanvas.
 *
 * @param {Array} layers — PixAlloy layer objects
 * @param {number} width — document width
 * @param {number} height — document height
 * @returns {OffscreenCanvas} — the flattened result
 */
export function flattenLayers(layers, width, height) {
  const comp = new OffscreenCanvas(width, height);
  const cx = comp.getContext('2d');
  for (const l of layers) {
    if (!l.vis || l.type === 'group') continue;
    cx.globalAlpha = l.op ?? 1;
    cx.globalCompositeOperation = l.blend || 'source-over';
    cx.drawImage(l.c, 0, 0);
    cx.globalAlpha = 1;
    cx.globalCompositeOperation = 'source-over';
  }
  return comp;
}
